import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react'
import SEO from '@/components/shared/SEO'
import { generatePlatformStructuredData } from '@/utils/structuredData'

interface SEOContextType {
  updateSEO: (props: SEOProps) => void
}

interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  ogImage?: string
  ogUrl?: string
  ogType?: 'website' | 'article' | 'profile'
  twitterCard?: 'summary' | 'summary_large_image'
  canonicalUrl?: string
  noIndex?: boolean
  structuredData?: object
}

const SEOContext = createContext<SEOContextType | undefined>(undefined)

export function SEOProvider({ children }: { children: ReactNode }) {
  const [seoProps, setSeoProps] = useState<SEOProps>({
    title: 'JeridSchool - Plateforme de gestion scolaire en Tunisie',
    description:
      'JeridSchool centralise la vie scolaire en Tunisie : emplois du temps, presences, examens, communication parents-ecole et outils IA pour les etablissements prives.',
    keywords:
      'gestion scolaire Tunisie, logiciel ecole privee, plateforme scolaire, emploi du temps automatique, communication parents ecole, logiciel etablissement scolaire',
    ogImage: 'https://www.jeridschool.com/og.png',
    ogUrl: 'https://www.jeridschool.com/',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    canonicalUrl: 'https://www.jeridschool.com/',
    structuredData: generatePlatformStructuredData(),
  })

  const updateSEO = useCallback((props: SEOProps) => {
    setSeoProps((prevProps) => {
      const nextProps = { ...prevProps, ...props }

      for (const key of Object.keys(nextProps) as Array<keyof SEOProps>) {
        if (!Object.is(prevProps[key], nextProps[key])) {
          return nextProps
        }
      }

      return prevProps
    })
  }, [])

  const contextValue = useMemo(() => ({ updateSEO }), [updateSEO])

  return (
    <SEOContext.Provider value={contextValue}>
      <SEO {...seoProps} />
      {children}
    </SEOContext.Provider>
  )
}

export function useSEO() {
  const context = useContext(SEOContext)
  if (context === undefined) {
    throw new Error('useSEO must be used within a SEOProvider')
  }
  return context
}
